import React, { useState } from "react";
import { Button } from "./Button";
import FormInput from "./FormInput";
import FormSelect from "./FormSelect";
import FormTextarea from "./FormTextarea";
import { CONNECTED_OUTCOMES } from "../session/outcomes";

/** Every outcome the disposition endpoint accepts, in the order a rep reads them. */
export const OUTCOME_OPTIONS = [
  { value: "interested", label: "Interested" },
  { value: "callback", label: "Callback requested" },
  { value: "not_interested", label: "Not interested" },
  { value: "no_answer", label: "No answer" },
  { value: "busy", label: "Busy" },
  { value: "voicemail", label: "Voicemail" },
  { value: "wrong_number", label: "Wrong number" },
  { value: "do_not_call", label: "Do not call" },
  { value: "completed", label: "Completed" },
  { value: "other", label: "Other" },
];

/**
 * The six wrap-up outcomes on keys 1–6 (DESIGN.md "Session · Wrap-up"). One
 * list with the session wrap-up, so an outcome has the same name and hint
 * whether the call started in a session or from the dock.
 */
export const WRAP_OUTCOMES = CONNECTED_OUTCOMES.map((outcome, index) => ({
  ...outcome,
  key: String(index + 1),
}));

export const STATUS_OPTIONS = [
  { value: "", label: "No change" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "do_not_call", label: "Do not call" },
  { value: "archived", label: "Archived" },
];

export const FOLLOW_UP_ACTIONS = [
  { value: "", label: "No follow-up" },
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Schedule Meeting" },
  { value: "quote", label: "Send Quote" },
];

export const EMPTY_DISPOSITION = {
  outcome: "",
  notes: "",
  followUpDate: "",
  followUpAction: "",
  statusChange: "",
};

/**
 * Field errors for a disposition, keyed by field name. An outcome is required;
 * a follow-up action and its date go together, and a callback needs a date
 * (without one the callback is lost: the contact keeps no next action).
 */
export const validateDisposition = (data) => {
  const errors = {};
  if (!data.outcome) errors.outcome = "Outcome is required";
  if (data.outcome === "callback" && !data.followUpDate) {
    errors.followUpDate = "Pick a date for the callback";
  } else if (data.followUpAction && !data.followUpDate) {
    errors.followUpDate = "Follow-up date is required when action is set";
  }
  if (data.followUpDate && !data.followUpAction) {
    errors.followUpAction = "Select a follow-up action for this date";
  }
  return errors;
};

/**
 * Call disposition form. Uncontrolled by default (`initialData`, reporting
 * edits through `onChange`); pass `value` with `onChange` to control it, so a
 * parent (outcome keys, a wrap-up sheet) can share the same state.
 */
const CallDispositionForm = ({
  customerId,
  callId,
  onSave,
  onChange,
  onCancel,
  isSaving = false,
  saveError = "",
  value,
  title = "Call Disposition",
  initialData = EMPTY_DISPOSITION,
}) => {
  const [localData, setLocalData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const controlled = value !== undefined;
  const formData = controlled ? value : localData;

  const handleChange = (e) => {
    const { name, value: fieldValue } = e.target;
    const nextData = {
      ...formData,
      [name]: fieldValue,
    };
    if (!controlled) setLocalData(nextData);
    onChange?.(nextData);

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateDisposition(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    onSave({
      customerId,
      callId,
      ...formData,
    });
  };

  return (
    <div className="sg-card p-4 sm:p-6">
      {title && (
        <h3 className="mb-5 text-[15px] font-semibold text-sg-ink">{title}</h3>
      )}
      <form onSubmit={handleSubmit} aria-busy={isSaving}>
        <fieldset disabled={isSaving} className="min-w-0 space-y-5">
          <div className="space-y-4">
            <FormTextarea
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              placeholder="What was said, what was agreed"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormSelect
                label="Call Outcome"
                name="outcome"
                value={formData.outcome}
                onChange={handleChange}
                options={[{ value: "", label: "Select outcome..." }, ...OUTCOME_OPTIONS]}
                required
                error={errors.outcome}
              />
              <FormSelect
                label="Update Status To"
                name="statusChange"
                value={formData.statusChange}
                onChange={handleChange}
                options={STATUS_OPTIONS}
              />
            </div>
          </div>

          <div className="border-t border-sg-line pt-5">
            <h4 className="sg-micro mb-3">Follow-up</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormSelect
                label="Action"
                name="followUpAction"
                value={formData.followUpAction}
                onChange={handleChange}
                options={FOLLOW_UP_ACTIONS}
                error={errors.followUpAction}
              />
              {/* The contact keeps a date, not a time, so the field asks for a date. */}
              <FormInput
                label="Date"
                name="followUpDate"
                type="date"
                value={formData.followUpDate}
                onChange={handleChange}
                error={errors.followUpDate}
              />
            </div>
          </div>

          {!callId && (
            <p id="disposition-save-blocked" className="text-sm text-sg-ink-2">
              Save turns on once the call record is available.
            </p>
          )}

          {saveError && (
            <div role="alert" className="sg-alert" data-tone="danger">
              {saveError}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            {onCancel && (
              <Button
                variant="secondary"
                onClick={onCancel}
                type="button"
                disabled={isSaving}
                className="min-h-11 px-6"
              >
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              type="submit"
              disabled={isSaving || !callId}
              aria-describedby={!callId ? "disposition-save-blocked" : undefined}
              className="min-h-11 px-6"
            >
              {isSaving ? "Saving…" : "Save disposition"}
            </Button>
          </div>
        </fieldset>
      </form>
    </div>
  );
};

export default CallDispositionForm;
